import { FormikErrors, useFormik } from "formik";
import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useMutation, useQuery } from "react-query";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Label } from "../../components/Label";
import { Input } from "../../components/Input";
import { Button } from "../../components/Button";

export const AddRestaurants: React.FC = () => {
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const navigate = useNavigate();

  const { mutate, isLoading, data } = useMutation(async (data: any) => {
    return await axios.post("/restaurant", data);
  });

  const { data: categories, isLoading: isCategoriesLoading } = useQuery(
    "Get Categories",
    async () => {
      const response = await axios.get("/categories");
      return response.data.categories;
    },
    {
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  const formik = useFormik({
    initialValues: {
      names: [
        { language: "ar", name: "" },
        { language: "en", name: "" },
        { language: "ur", name: "" },
      ],
      logo: null,
      token: "",
      branchs_token: "",
      system_type: "",
      name_api: "",
      categoryId: null,
      warrantyPeiod: 0,
      recoveryPeiod: 0,
      replacementPeriod: 0,
    },
    validationSchema: Yup.object({
      names: Yup.array()
        .of(
          Yup.object({
            language: Yup.string().required(),
            name: Yup.string(),
          })
        )
        .min(1),
      logo: Yup.mixed().required("Required"),
      token: Yup.string().required("Required"),
      branchs_token: Yup.string().required("Required"),
      system_type: Yup.string().required("Required"),
      categoryId: Yup.number().nullable(),
      warrantyPeiod: Yup.number().required("Required"),
      recoveryPeiod: Yup.number().required("Required"),
      replacementPeriod: Yup.number().required("Required"),
    }),
    onSubmit: async (values) => {
      const formData = new FormData();

      const names = values.names.filter((n) => n.name.trim() !== "");
      formData.append("names", JSON.stringify(names));

      if (values.logo) {
        formData.append("restaurantLogo", values.logo as File);
      }
      formData.append("token", values.token);
      formData.append("branchs_token", values.branchs_token);
      formData.append("system_type", values.system_type);
      formData.append("categoryId", String(values.categoryId));
      formData.append("warrantyPeiod", String(values.warrantyPeiod));
      formData.append("recoveryPeiod", String(values.recoveryPeiod));
      formData.append("replacementPeriod", String(values.replacementPeriod));

      try {
        await mutate(formData);
      } catch (error) {
        console.log(error);
      }
    },
  });

  useEffect(() => {
    if (!isLoading && data) {
      navigate("/dashboard/restaurants");
    }
  }, [isLoading, data, navigate]);

  return (
    <div className="flex">
      <form
        className="flex w-8/12 flex-col gap-4 "
        onSubmit={formik.handleSubmit}
      >
        <>
          <h3 className="text-3xl font-medium py-5">Add New Restaurant</h3>
          {formik.values.names.map((item, index) => (
            <div className="w-full" key={index}>
              <Label htmlFor={`names.${index}.name`}>
                Restaurant Name ({item.language.toUpperCase()})
              </Label>
              <Input
                type="text"
                id={`names.${index}.name`}
                className={`w-full ${
                  item.language === "ur" ? " h-full font-urdu" : ""
                }`}
                {...formik.getFieldProps(`names.${index}.name`)}
                error={
                  formik.touched.names?.[index]?.name
                    ? (
                        formik.errors.names?.[index] as FormikErrors<{
                          language: string;
                          name: string;
                        }>
                      )?.name || ""
                    : ""
                }
              />
            </div>
          ))}

          <div className="w-full">
            <Label htmlFor="logo">Logo</Label>
            <input
              type="file"
              id="logo"
              name="logo"
              accept="image/*"
              onChange={(event) => {
                const file = event.currentTarget.files?.[0];
                if (file) {
                  formik.setFieldValue("logo", file);
                  setLogoPreview(URL.createObjectURL(file));
                }
              }}
              className="w-full border border-gray-300 p-2 rounded"
            />
            {formik.touched.logo && formik.errors.logo && (
              <span className="px-1 text-xs text-red-500">
                {formik.errors.logo}
              </span>
            )}

            {logoPreview && (
              <div className="mt-2">
                <img
                  src={logoPreview}
                  alt="Logo Preview"
                  className="h-20 object-contain border"
                />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1 text-sm">
            <Label htmlFor="token">Token</Label>
            <textarea
              placeholder="Token"
              className={`px-5 py-3 rounded-md focus:outline-none h-20 bg-gray-50 border-gray-300 border-[1px] text-gray-900 ${
                formik.touched.token && formik.errors.token
                  ? " border border-red-500 bg-red-50 text-red-900 placeholder-red-700"
                  : ""
              }`}
              id="token"
              {...formik.getFieldProps("token")}
            ></textarea>
            {formik.touched.token && (
              <span className="px-1 text-xs  text-red-500">
                {formik.touched.token ? formik.errors.token : ""}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1 text-sm">
            <Label htmlFor="branchs_token">Branchs Token</Label>
            <textarea
              placeholder="Branchs Token"
              className={`px-5 py-3 rounded-md focus:outline-none h-20 bg-gray-50 border-gray-300 border-[1px] text-gray-900 ${
                formik.touched.branchs_token && formik.errors.branchs_token
                  ? " border border-red-500 bg-red-50 text-red-900 placeholder-red-700"
                  : ""
              }`}
              id="branchs_token"
              {...formik.getFieldProps("branchs_token")}
            ></textarea>
            {formik.touched.branchs_token && (
              <span className="px-1 text-xs  text-red-500">
                {formik.touched.branchs_token
                  ? formik.errors.branchs_token
                  : ""}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1 text-sm">
            <Label htmlFor="system_type">System Type</Label>
            <select
              className={`border-gray-300 border-[1px] px-5 py-3 rounded-md focus:outline-none bg-gray-50 text-gray-900 ${
                formik.touched.system_type
                  ? formik.errors.system_type
                    ? "border border-red-500 bg-red-50 text-red-900 placeholder-red-700"
                    : ""
                  : ""
              }`}
              id="type"
              {...formik.getFieldProps("system_type")}
            >
              <option disabled value="">
                Choose System Type
              </option>
              <option value="Foodics">Foodics</option>
            </select>
            {
              <span className="px-1 text-xs  text-red-500">
                {formik.touched.system_type ? formik.errors.system_type : ""}
              </span>
            }
          </div>

          <div className="flex flex-col gap-1 text-sm">
            <Label htmlFor="categoryId">Category</Label>
            {!isCategoriesLoading && (
              <>
                <select
                  className={`border-gray-300 border-[1px] px-5 py-3 rounded-md focus:outline-none bg-gray-50 text-gray-900 ${
                    formik.touched.categoryId
                      ? formik.errors.categoryId
                        ? "border border-red-500 bg-red-50 text-red-900 placeholder-red-700"
                        : ""
                      : ""
                  }`}
                  id="categoryId"
                  {...formik.getFieldProps("categoryId")}
                >
                  <option value="">Choose Category</option>
                  {categories?.map((category: any) => (
                    <option key={category.id} value={category.id}>
                      {category?.name ||
                        category?.names?.find((n: any) => n.language === "en")
                          ?.name ||
                        "-"}
                    </option>
                  ))}
                </select>
                {
                  <span className="px-1 text-xs  text-red-500">
                    {formik.touched.categoryId ? formik.errors.categoryId : ""}
                  </span>
                }
              </>
            )}
          </div>

          <div className="w-full">
            <Label htmlFor="warrantyPeiod">Warranty Peiod</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                id="warrantyPeiod"
                className="w-full"
                {...formik.getFieldProps("warrantyPeiod")}
                error={
                  formik.touched.warrantyPeiod
                    ? formik.errors.warrantyPeiod
                    : ""
                }
              />
              <p className="font-semibold">Day</p>
            </div>
          </div>

          <div className="w-full">
            <Label htmlFor="recoveryPeiod">Recovery Peiod</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                id="recoveryPeiod"
                className="w-full"
                {...formik.getFieldProps("recoveryPeiod")}
                error={
                  formik.touched.recoveryPeiod
                    ? formik.errors.recoveryPeiod
                    : ""
                }
              />{" "}
              <p className="font-semibold">Day</p>
            </div>
          </div>

          <div className="w-full">
            <Label htmlFor="replacementPeriod">Replacement Period</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                id="replacementPeriod"
                className="w-full"
                {...formik.getFieldProps("replacementPeriod")}
                error={
                  formik.touched.replacementPeriod
                    ? formik.errors.replacementPeriod
                    : ""
                }
              />{" "}
              <p className="font-semibold">Day</p>
            </div>
          </div>

          <div className="flex gap-3 mb-10">
            <Button green type="submit" loading={isLoading}>
              Add
            </Button>
            <Button
              type="button"
              lightRed
              onClick={() => navigate("/dashboard/restaurants")}
            >
              Cancel
            </Button>
          </div>
        </>
      </form>
    </div>
  );
};
